import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import NewsletterForm from '@/components/feature/NewsletterForm';
import useSeo from '@/hooks/useSeo';

const positions = [
  {
    icon: 'ri-radar-line',
    title: '只做线索整理',
    desc: '我们不生产爆料，只把公开的讨论、回应与媒体报道整理成可追溯的档案。',
  },
  {
    icon: 'ri-eye-line',
    title: '面向普通读者',
    desc: '用时间线和分级判定代替情绪化标题，让不熟悉圈内黑话的人也能看懂。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '保持中立语气',
    desc: '不站队、不预设立场，争议未明时宁可不写结论，也不跟风定性。',
  },
];

const rules = [
  '只引用可指向来源的信息，匿名爆料一律标注出处状态。',
  '不使用真实姓名指向非公众人物，不公开住址、联系方式等隐私。',
  '涉及未成年人的内容不做展开，也不做任何推测性描述。',
  '判定结论会随新证据更新，历史版本保留修改记录。',
  '对读者提交的更正会逐条核对，确认有误的立即修改并说明。',
  '拒绝接受以攻击、引流为目的的内容合作。',
];

const voices = [
  {
    name: '周屿',
    role: '高校研究生',
    text: '写论文养成了查来源的习惯，这里的分级判定刚好契合，看八卦也能很讲证据。',
  },
  {
    name: '小葵',
    role: '插画师',
    text: '排版读起来很舒服，没有满屏感叹号，时间线做得尤其清楚。',
  },
  {
    name: '老陈',
    role: '资深吃瓜群众',
    text: '吃过太多次反转的亏，现在习惯先来这儿看看判定是「存疑」还是「已确认」。',
  },
  {
    name: 'Momo',
    role: '学生',
    text: '词典那篇对我这种刚入门的人太友好了，终于能看懂评论区在说什么。',
  },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们｜西瓜瓜田 娱乐线索整理编辑部',
    description:
      '西瓜瓜田是一个专注娱乐线索整理与真伪分析的编辑部：介绍我们的网站定位、发展理念、内容原则、读者口碑与联系方式。',
    keywords: '西瓜瓜田, 关于我们, 娱乐线索整理, 内容原则, 真伪分析',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们｜西瓜瓜田',
        description: '西瓜瓜田的网站定位、发展理念与内容原则。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系西瓜瓜田',
        description: '通过邮箱联系西瓜瓜田编辑部。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiguaguatian.example.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://xiguaguatian.example.com/about' },
        ],
      },
    ],
  });

  return (
    <>
      <SiteHeader />
      <main>
        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                  关于西瓜瓜田
                  <span className="mt-3 block text-lg font-bold text-primary-700 md:text-xl">
                    一个想把瓜吃明白的编辑部
                  </span>
                </h1>
                <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                  西瓜瓜田创立于 2024 年，起因很简单：身边的朋友总是被同一件事的不同说法绕晕。我们于是决定，把公开信息整理成一条能看懂的线——谁先说的、什么时候说的、有没有回应、结论目前到哪一步。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  我们不做最快的那一个，只想做最清楚的那一个。热闹是别人的，判断力是你自己的。
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-300 bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=flat%20vector%20editorial%20illustration%20of%20a%20warm%20writing%20desk%20with%20notebooks%20a%20watermelon%20slice%20and%20a%20desk%20lamp%2C%20warm%20cream%20paper%20background%2C%20tomato%20red%20amber%20gold%20and%20deep%20teal%20palette%2C%20subtle%20grain%20texture%2C%20minimal%20geometric%20shapes%2C%20no%20text&width=1000&height=750&seq=xigua-about-visual&orientation=landscape"
                    alt="西瓜瓜田编辑部整理线索的工作场景插画"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-6">
            <SectionHeading
              kicker="网站定位"
              title="我们在做一件什么样的事"
              desc="三个关键词可以概括：可追溯、看得懂、不站队。"
            />
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {positions.map((item, index) => (
                <Reveal key={item.title} delay={index * 80}>
                  <article className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                    <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-2xl`} />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  kicker="内容原则"
                  title="六条我们不会越过的线"
                  desc="这些规则写在编辑手册的第一页，每一条内容上线前都会对照检查。"
                />
                <div className="mt-8 rounded-lg bg-accent-100 p-6">
                  <p className="font-heading text-base font-bold text-accent-900">发展理念</p>
                  <p className="mt-3 text-sm leading-relaxed text-accent-900">
                    娱乐新闻不该只有情绪。我们希望通过长期的线索整理，慢慢积累一份可检索、可回溯的娱乐事件档案，让后来的人查得到、对得上。
                  </p>
                </div>
              </div>
              <div className="lg:col-span-7">
                <ol className="space-y-4">
                  {rules.map((rule, index) => (
                    <li
                      key={rule}
                      className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-600 text-sm font-bold text-background-50">
                        {index + 1}
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-700">{rule}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-6">
            <SectionHeading
              kicker="读者口碑"
              title="他们眼里的西瓜瓜田"
              desc="来自订阅者与读者的部分反馈，已获得授权匿名使用。"
              align="center"
            />
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {voices.map((voice, index) => (
                <Reveal key={voice.name} delay={index * 70}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                    <i className="ri-double-quotes-l text-2xl text-accent-500" />
                    <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                      {voice.text}
                    </blockquote>
                    <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-600 text-sm font-bold text-background-50">
                        {voice.name.slice(0, 1)}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-950">{voice.name}</span>
                        <span className="block text-xs text-foreground-500">{voice.role}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  kicker="联系方式"
                  title="有更正或线索，随时找我们"
                  desc="如果你发现档案里有误，或者手里有更可靠的来源，欢迎写邮件给我们，我们会在核对后更新内容。"
                />
                <div className="mt-8 space-y-4 text-sm">
                  <p className="flex items-center gap-3 text-foreground-700">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    <a href="mailto:gua@xiguaguatian.example.com" className="cursor-pointer hover:text-primary-700">
                      gua@xiguaguatian.example.com
                    </a>
                  </p>
                  <p className="flex items-center gap-3 text-foreground-700">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-time-line text-lg" />
                    </span>
                    工作日 10:00 – 19:00 回复邮件
                  </p>
                </div>
                <p className="mt-6 text-xs leading-relaxed text-foreground-500">
                  我们不接受带有攻击性、要求曝光隐私或商业引流的合作请求。
                </p>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950">订阅吃瓜速递</h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    每周一封，只挑三条值得追的线索，附上时间线与判定更新。
                  </p>
                  <div className="mt-5">
                    <NewsletterForm />
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-4 rounded-lg bg-secondary-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm leading-relaxed text-secondary-900">
                    想先看看我们整理的成果？从今日热瓜开始最快。
                  </p>
                  <Link
                    to="/#hot"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-secondary-600 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
                  >
                    去看今日热瓜
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}